import React, { useEffect, useState } from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import { v4 as uuidv4 } from "uuid";
import { Icon } from '@iconify/react';
import AddEditTaskModal from "../child/AddEditTaskModal";
import useTicketApi from "../../api/useTicketApi";
import TicketColumn from "./TicketColumn";
import CallConfirmationDialog from "../modals/CallConfirmationDialog";
import WhatsAppConfirmationDialog from "../modals/WhatsAppConfirmationDialog";
import AdminWiseDetailsModal from "../modals/AdminWiseDetailsModal";
import { Dialog } from "@mui/material";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import useUsersApi from "../../api/useUsersApi";

const statusList = ["pending", "accepted", "scheduled", "completed"];

const initialData = {
  columns: {
    pending: {
      id: "pending",
      title: "Pending",
      taskIds: [],
    },
    accepted: {
      id: "accepted",
      title: "Accepted",
      taskIds: [],
    },
    scheduled: {
      id: "scheduled",
      title: "Scheduled",
      taskIds: [],
    },
    completed: {
      id: "completed",
      title: "Completed",
      taskIds: [],
    },
  },
  tasks: {},
  columnOrder: ["pending", "accepted", "scheduled", "completed"],
};

const TicketKanbanBoard = () => {
  const { permissions, profile, isActionAllowed } = useAuth();
  const { refresTicket, updateTicket, loading } = useTicketApi();
  const { makeCall, sendWhatsAppNotification } = useUsersApi();
  const [callLoading, setCallLoading] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [data, setData] = useState(initialData);
  const [totalCounts, setTotalCounts] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentColumn, setCurrentColumn] = useState(null);
  const [openPhone, setOpenPhone] = useState(false);
  const [reason, setReason] = useState("");
  const [openSchedule, setOpenSchedule] = useState(false);
  const [openSetComplete, setOpenSetComplete] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [dropResult, setDropResult] = useState(null);
  const [hasMore, setHasMore] = useState({
    pending: true,
    accepted: true,
    scheduled: true,
    completed: true,
  });

  const [columnPages, setColumnPages] = useState({
    pending: 1,
    accepted: 1,
    scheduled: 1,
    completed: 1,
  });
  const [isReschedule, setIsReschedule] = useState(false); // Add this state
  const [openAdminWiseDetails, setOpenAdminWiseDetails] = useState(false);

  const showPhone = (task) => {
    setSelectedLead(task);
    setOpenPhone(true);
  };

  const showWhatsApp = (task) => {
    setSelectedLead(task);
    setWhatsappDialogOpen(true);
  };

  const handleCallConfirm = async () => {
    if (!selectedLead) return;
    const phoneNumber = selectedLead?.user_id?.phone_number;
    if (!phoneNumber) return;
    try {
      setCallLoading(true);
      await makeCall(phoneNumber);
      toast.success('Call initiated successfully!');
    } catch (err) {
      // error handled by useApi hook
    } finally {
      setCallLoading(false);
      setOpenPhone(false);
    }
  };

  const handleWhatsAppConfirm = async () => {
    if (!selectedLead) return;
    const phoneNumber = selectedLead?.user_id?.phone_number;
    if (!phoneNumber) return;
    try {
      setWhatsappLoading(true);
      await sendWhatsAppNotification(phoneNumber);
      toast.success('WhatsApp notification sent successfully!');
    } catch (err) {
      // error handled by useApi hook
    } finally {
      setWhatsappLoading(false);
      setWhatsappDialogOpen(false);
    }
  };

  const updateTicketStatus = async (id, status) => {
    const res = await updateTicket(id, { status });
    toast.success("Leads Updated Successfully!");
    return res.data.payload.find_tickets || [];
  };

  const fetchLeadsByStatus = async (status, page = 1) => {
    const res = await refresTicket(status, page);
    return res.data.payload || {};
  };

  const mapLeadsToData = (status, leads) => {
    const tasks = {};
    const taskIds = [];

    leads.forEach((lead) => {
      const taskId = lead._id;
      tasks[taskId] = {
        id: taskId,
        title: lead.title || "Untitled",
        description: lead.description || "",
        date: lead.createdAt || "",
        ...lead,
      };
      taskIds.push(taskId);
    });

    return { tasks, taskIds };
  };

  const fetchAllLeads = async () => {
    const allTasks = {};
    const allColumns = {
      pending: { id: "pending", title: "Pending", taskIds: [] },
      accepted: { id: "accepted", title: "Accepted", taskIds: [] },
      scheduled: { id: "scheduled", title: "Scheduled", taskIds: [] },
      completed: { id: "completed", title: "Completed", taskIds: [] },
    };

    await Promise.all(
      statusList.map(async (status) => {
        const leads = await fetchLeadsByStatus(status);
        const { tasks, taskIds } = mapLeadsToData(
          status,
          leads?.find_tickets || []
        );
        setTotalCounts((prev) => ({
          ...prev,
          [status]: leads?.total_tickets || 0,
        })); 
        Object.assign(allTasks, tasks);
        allColumns[status].taskIds = taskIds;
        if (leads?.total_pages <= leads?.current_page) {
          setHasMore((prev) => ({
            ...prev,
            [status]: false,
          }));
        }
      })
    );

    setData({
      tasks: allTasks,
      columns: allColumns,
      columnOrder: ["pending", "accepted", "scheduled", "completed"],
    });
  };

  const fetchScheduledLeads = async () => {
    const allTasks = {};
    const allColumns = {
      scheduled: { id: "scheduled", title: "Scheduled", taskIds: [] },
    };

    // Fetch only scheduled tickets
    const leads = await fetchLeadsByStatus("scheduled");

    // Map data into your task/column format
    const { tasks, taskIds } = mapLeadsToData(
      "scheduled",
      leads?.find_tickets || []
    );

    Object.assign(allTasks, tasks);
    allColumns["scheduled"].taskIds = taskIds;

    // Update pagination info
    if (leads?.total_pages <= leads?.current_page) {
      setHasMore((prev) => ({
        ...prev,
        scheduled: false,
      }));
    }

    // Update only scheduled data in state
    setData((prev) => ({
      ...prev,
      tasks: { ...prev.tasks, ...tasks },
      columns: {
        ...prev.columns,
        scheduled: {
          ...prev.columns.scheduled,
          taskIds,
        },
      },
    }));
  };

  const refreshSingleColumn = async (status) => {
    const leads = await fetchLeadsByStatus(status);
    const { tasks: newTasks, taskIds } = mapLeadsToData(
      status,
      leads?.find_tickets || []
    );

    setData((prevData) => ({
      ...prevData,
      tasks: {
        ...prevData.tasks,
        ...newTasks,
      },
      columns: {
        ...prevData.columns,
        [status]: {
          ...prevData.columns[status],
          taskIds,
        },
      },
    }));
  };

  useEffect(() => {
    fetchAllLeads();
  }, []);

  const handleFinalMove = (updatedTask = null) => {
    const { destination, source, draggableId } = dropResult;
    const startColumn = data.columns[source.droppableId];
    const endColumn = data.columns[destination.droppableId];

    // Remove from source column
    const startTaskIds = Array.from(startColumn.taskIds);
    startTaskIds.splice(source.index, 1);
    const newStart = {
      ...startColumn,
      taskIds: startTaskIds,
    };

    // Add to destination column
    const endTaskIds = Array.from(endColumn.taskIds);
    endTaskIds.splice(destination.index, 0, draggableId);
    const newEnd = {
      ...endColumn,
      taskIds: endTaskIds,
    };

    // If updatedTask is provided, update the task in the tasks object
    const newTasks = updatedTask
      ? {
        ...data.tasks,
        [draggableId]: { ...updatedTask, id: updatedTask._id },
      }
      : data.tasks;

    // Final updated data
    const updatedData = {
      ...data,
      tasks: newTasks,
      columns: {
        ...data.columns,
        [newStart.id]: newStart,
        [newEnd.id]: newEnd,
      },
    };

    setData(updatedData);

    // Update total counts
    setTotalCounts((prev) => ({
      ...prev,
      [source.droppableId]: Math.max(0, (prev[source.droppableId] || 0) - 1),
      [destination.droppableId]: (prev[destination.droppableId] || 0) + 1,
    }));

    // Optional refresh logic
    if (
      source.droppableId === "pending" &&
      destination.droppableId === "accepted" &&
      newStart.taskIds.length === 0
    ) {
      refreshSingleColumn("pending");
    }
  };

  useEffect(() => {
    if (!dropResult) return;
    if (dropResult?.destination?.droppableId === "accepted") handleFinalMove();
  }, [dropResult]);

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    const startColumn = data.columns[source.droppableId];
    const endColumn = data.columns[destination.droppableId];

    // Moving within the same column
    if (startColumn === endColumn) {
      const newTaskIds = Array.from(startColumn.taskIds);
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);

      const newColumn = {
        ...startColumn,
        taskIds: newTaskIds,
      };

      setData({
        ...data,
        columns: {
          ...data.columns,
          [newColumn.id]: newColumn,
        },
      });
      return;
    }

    const stageOrder = {
      pending: 1,
      accepted: 2,
      scheduled: 3,
      completed: 3,
    };

    if (stageOrder[destination.droppableId] < stageOrder[source.droppableId]) {
      return;
    }

    // Optional: update backend status
    if (
      source.droppableId === "pending" &&
      destination.droppableId === "accepted"
    ) {
      await updateTicketStatus(draggableId, "accept");
      setSelectedTicket(draggableId);
      setDropResult({ destination, source, draggableId });
    } else if (
      source.droppableId === "accepted" &&
      destination.droppableId === "scheduled"
    ) {
      setSelectedTicket(draggableId);
      setOpenSchedule(true);
      setDropResult({ destination, source, draggableId });
      // updateTicketStatus(draggableId, 'scheduled');
    } else if (
      source.droppableId === "scheduled" &&
      destination.droppableId === "completed"
    ) {
      setSelectedTicket(draggableId);
      setOpenSetComplete(true);
      setDropResult({ destination, source, draggableId });
      // updateTicketStatus(draggableId, 'complete');
    } else if (
      source.droppableId === "accepted" &&
      destination.droppableId === "completed"
    ) {
      setSelectedTicket(draggableId);
      setOpenSetComplete(true);
      setDropResult({ destination, source, draggableId });
      // updateTicketStatus(draggableId, 'complete');
    } else {
      return;
    }
  };

  const handleAddTask = (columnId) => {
    setCurrentTask(null);
    setCurrentColumn(columnId);
    setShowModal(true);
  };

  const handleEditTask = (taskId, columnId) => {
    const task = data.tasks[taskId];
    setCurrentTask({ ...task, columnId });
    setCurrentColumn(columnId);
    setShowModal(true);
  };

  const handleDeleteTask = (taskId, columnId) => {
    const column = data.columns[columnId];
    const newTaskIds = column.taskIds.filter((id) => id !== taskId);
    const newColumn = {
      ...column,
      taskIds: newTaskIds,
    };
    const newTasks = { ...data.tasks };
    delete newTasks[taskId];

    setData({
      ...data,
      columns: {
        ...data.columns,
        [newColumn.id]: newColumn,
      },
      tasks: newTasks,
    });
  };

  const handleDuplicateTask = (taskId, columnId) => {
    const task = data.tasks[taskId];
    const newId = uuidv4();
    const duplicatedTask = { ...task, id: newId };
    const newTasks = {
      ...data.tasks,
      [newId]: duplicatedTask,
    };
    const column = data.columns[columnId];
    const newTaskIds = Array.from(column.taskIds);
    newTaskIds.splice(0, 0, newId); // Add to the top
    const newColumn = {
      ...column,
      taskIds: newTaskIds,
    };

    setData({
      ...data,
      columns: {
        ...data.columns,
        [newColumn.id]: newColumn,
      },
      tasks: newTasks,
    });
  };

  // If implementing column duplication
  const handleDuplicateColumn = (columnId) => {
    const column = data.columns[columnId];
    const newColumnId = uuidv4();
    const newTaskIds = [...column.taskIds]; // Duplicate task assignments as-is

    const newColumn = {
      ...column,
      id: newColumnId,
      title: `${column.title} (Copy)`,
      taskIds: newTaskIds,
    };

    setData((prevData) => ({
      ...prevData,
      columns: {
        ...prevData.columns,
        [newColumnId]: newColumn,
      },
      columnOrder: [...prevData.columnOrder, newColumnId],
    }));
  };

  const handleSaveTask = (task, isEdit) => {
    if (isEdit) {
      // Update existing task
      const updatedTasks = {
        ...data.tasks,
        [task.id]: task,
      };
      setData({
        ...data,
        tasks: updatedTasks,
      });
    } else {
      // Add new task
      const newId = uuidv4();
      const newTask = { ...task, id: newId };
      const newTasks = {
        ...data.tasks,
        [newId]: newTask,
      };
      const column = data.columns[currentColumn];
      const newTaskIds = Array.from(column.taskIds);
      newTaskIds.push(newId);
      const newColumn = {
        ...column,
        taskIds: newTaskIds,
      };
      setData({
        ...data,
        columns: {
          ...data.columns,
          [newColumn.id]: newColumn,
        },
        tasks: newTasks,
      });

      // Update total counts
      setTotalCounts((prev) => ({
        ...prev,
        [currentColumn]: (prev[currentColumn] || 0) + 1,
      }));
    }
    setShowModal(false);
  };

  const handleReschedule = (taskId) => {
    setSelectedTicket(taskId);
    setOpenSchedule(true);
    setIsReschedule(true);
    setReason("");
    setSelectedDate("");
  };

  const handleMoveAcceptToSchedule = async (e) => {
    e.preventDefault();
    setOpenSchedule(false);
    // If rescheduling, status stays 'schedule'
    const res = await updateTicket(selectedTicket, {
      status: "schedule",
      schedule: selectedDate,
      reason: reason,
    });
    setReason("");
    setSelectedDate("");
    setIsReschedule(false); // Reset
    toast.success(
      isReschedule
        ? "Ticket Rescheduled Successfully!"
        : "Ticket Scheduled Successfully!"
    );
    if (isReschedule && res?.data?.payload) {
      fetchScheduledLeads();
      //   setData((prev) => ({
      //     ...prev,
      //     tasks: {
      //       ...prev.tasks,
      //       [res.data.payload._id]: {
      //         ...res.data.payload,
      //         id: res.data.payload._id,
      //       },
      //     },
      //   }));
    } else {
      handleFinalMove(res?.data?.payload);
    }
  };

  const handleMoveScheduleToComplete = async (e) => {
    e.preventDefault();
    setOpenSetComplete(false);
    await updateTicket(selectedTicket, {
      status: "complete",
      reason: reason,
    });
    setReason("");
    toast.success("Ticket Completed Successfully!");
    handleFinalMove();
  };

  const loadMoreLeads = async (status) => {
    const nextPage = columnPages[status] + 1;

    const response = await fetchLeadsByStatus(status, nextPage);
    const leads = response?.find_tickets || [];

    if (leads.length === 0) {
      setHasMore((prev) => ({
        ...prev,
        [status]: false,
      }));
      return;
    }

    // Filter out duplicates
    const existingIds = new Set(data.columns[status].taskIds);
    const uniqueLeads = leads.filter((lead) => !existingIds.has(lead._id));

    if (uniqueLeads.length === 0) {
      setHasMore((prev) => ({
        ...prev,
        [status]: false,
      }));
      return;
    }

    const { tasks: newTasks, taskIds: newTaskIds } = mapLeadsToData(
      status,
      uniqueLeads
    );

    setData((prevData) => ({
      ...prevData,
      tasks: {
        ...prevData.tasks,
        ...newTasks,
      },
      columns: {
        ...prevData.columns,
        [status]: {
          ...prevData.columns[status],
          taskIds: [...prevData.columns[status].taskIds, ...newTaskIds],
        },
      },
    }));

    setColumnPages((prevPages) => ({
      ...prevPages,
      [status]: nextPage,
    }));
  };

  return (
    <>
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50"
          style={{ zIndex: 1050 }}
        >
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">Tickets</h6>
        {profile?.user_type === 'superadmin' && (
          <button
            className="btn btn-sm btn-primary-600 px-16 py-8 radius-8 d-flex align-items-center gap-2"
            onClick={() => setOpenAdminWiseDetails(true)}
          >
            <Icon icon="solar:chart-outline" className="text-lg" />
            View Admin Wise Details
          </button>
        )}
      </div>
      <div className="overflow-x-auto scroll-sm pb-8">
        <div className="kanban-wrapper p-4">
          <DragDropContext
            onDragEnd={isActionAllowed("Tickets", "edit") ? onDragEnd : null}
          >
            <div
              className="d-flex align-items-start gap-4"
              style={{ overflowX: "auto" }}
            >
              {data.columnOrder.map((columnId) => {
                const column = data.columns[columnId];
                const tasks = column.taskIds.map(
                  (taskId) => data.tasks[taskId]
                );

                return (
                    <TicketColumn
                      columnId={column.id}
                      showPhone={showPhone}
                      showWhatsApp={showWhatsApp}
                      column={column}
                    tasks={tasks}
                    onAddTask={handleAddTask}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    onDuplicateTask={handleDuplicateTask}
                    onDuplicateColumn={handleDuplicateColumn}
                    loadMoreLeads={() => loadMoreLeads(column.id)}
                    hasMore={hasMore[column.id]}
                    loading={loading}
                    onReschedule={handleReschedule} // <-- Pass handler
                    totalCounts={totalCounts}
                  />
                );
              })}
            </div>
          </DragDropContext>

          {/* Add/Edit Task Modal */}
          <AddEditTaskModal
            show={showModal}
            handleClose={() => setShowModal(false)}
            handleSave={handleSaveTask}
            task={currentTask}
          />
        </div>
      </div>
      {selectedLead && (
        <CallConfirmationDialog
          open={openPhone}
          onClose={() => setOpenPhone(false)}
          onConfirm={handleCallConfirm}
          userName={selectedLead?.user_id?.user_name}
          phoneNumber={selectedLead?.user_id?.phone_number}
          loading={callLoading}
        />
      )}

      {selectedLead && (
        <WhatsAppConfirmationDialog
          open={whatsappDialogOpen}
          onClose={() => setWhatsappDialogOpen(false)}
          onConfirm={handleWhatsAppConfirm}
          userName={selectedLead?.user_id?.user_name}
          phoneNumber={selectedLead?.user_id?.phone_number}
          loading={whatsappLoading}
        />
      )}

      <Dialog
        open={openSchedule}
        onClose={() => {
          setOpenSchedule(false);
          setIsReschedule(false);
        }}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "500px",
          },
        }}
      >
        <form className="card mb-0" onSubmit={handleMoveAcceptToSchedule}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="card-title mb-0">
              {isReschedule ? "Reschedule Ticket" : "Schedule Ticket"}
            </h6>
            <button
              type="button"
              className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center"
              onClick={() => {
                setOpenSchedule(false);
                setIsReschedule(false);
              }}
            >
              <MdClose size={18} />
            </button>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label fw-bold text-sm mb-1">
                Select Date
              </label>
              <input
                required
                type="date"
                className="form-control"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-bold text-sm mb-1">Reason</label>
              <textarea
                required
                className="form-control"
                rows="4"
                placeholder="Write reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button className="btn btn-primary px-4 py-2 radius-8 text-md">
                {isReschedule ? "Reschedule" : "Continue"}
              </button>
            </div>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={openSetComplete}
        onClose={() => setOpenSetComplete(false)}
        maxWidth={false} // Disable MUI's default maxWidth
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            width: "500px", // Custom width here
          },
        }}
      >
        <form
          className="card mb-0"
          onSubmit={(e) => handleMoveScheduleToComplete(e)}
        >
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="card-title mb-0">Complete Ticket</h6>
            <button
              type="button"
              className="btn btn-sm btn-light p-1 d-flex justify-content-center align-items-center"
              onClick={() => setOpenSetComplete(false)}
            >
              <MdClose size={18} />
            </button>
          </div>

          <div className="card-body">
            <div className="mb-3">
              <label className="form-label fw-bold text-sm mb-1">Reason</label>
              <textarea
                className="form-control"
                rows="4"
                placeholder="Write reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              />
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button className="btn btn-primary px-4 py-2 radius-8 text-md">
                Complete
              </button>
            </div>
          </div>
        </form>
      </Dialog>

      <AdminWiseDetailsModal
        open={openAdminWiseDetails}
        onClose={() => setOpenAdminWiseDetails(false)}
        filter="tickets"
      />
    </>
  );
};

export default TicketKanbanBoard;
